import { useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

export interface KeyboardShortcutsOptions {
  onToggleHelp: () => void
}

function isInputFocused(): boolean {
  const el = document.activeElement
  if (!el) return false
  const tag = el.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
  if ((el as HTMLElement).isContentEditable) return true
  return false
}

export function useKeyboardShortcuts({ onToggleHelp }: KeyboardShortcutsOptions) {
  const navigate = useNavigate()
  const location = useLocation()

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey

      // Cmd/Ctrl+K — open search (always active, even in inputs)
      if (meta && e.key === 'k') {
        e.preventDefault()
        navigate('/search')
        return
      }

      // Escape — close modals/overlays by navigating back if on overlay routes
      if (e.key === 'Escape') {
        // Let native handlers (dialogs, inputs) handle Escape first
        if (isInputFocused()) return
        // If on search, go back
        if (location.pathname === '/search') {
          e.preventDefault()
          navigate(-1)
          return
        }
        return
      }

      // All remaining shortcuts are disabled when an input is focused
      if (isInputFocused()) return

      // ? — show keyboard shortcuts help
      if (e.key === '?' && !meta && !e.altKey) {
        e.preventDefault()
        onToggleHelp()
        return
      }

      // N — new contact
      if (e.key === 'n' && !meta && !e.altKey && !e.shiftKey) {
        e.preventDefault()
        navigate('/contacts/new')
        return
      }

      // G then H — go home (use single key for simplicity)
      // H — go home
      if (e.key === 'h' && !meta && !e.altKey && !e.shiftKey) {
        e.preventDefault()
        navigate('/')
        return
      }

      // C — go to contacts
      if (e.key === 'c' && !meta && !e.altKey && !e.shiftKey) {
        e.preventDefault()
        navigate('/contacts')
        return
      }

      // R — go to reminders
      if (e.key === 'r' && !meta && !e.altKey && !e.shiftKey) {
        e.preventDefault()
        navigate('/reminders')
        return
      }

      // S — go to settings
      if (e.key === 's' && !meta && !e.altKey && !e.shiftKey) {
        e.preventDefault()
        navigate('/settings')
        return
      }

      // L — log interaction
      if (e.key === 'l' && !meta && !e.altKey && !e.shiftKey) {
        e.preventDefault()
        navigate('/log-interaction')
        return
      }
    },
    [navigate, location.pathname, onToggleHelp],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])
}
